import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Tippy from '../ReactTippy';
import { iosCheck } from '../../utilities/stringUtil';
import { messageUtility } from '../../utilities/messageUtility';

export const VerticalScrollBodyTable = ({
  tableClassName = '',
  tableStyle = {},
  headerClassName = '',
  headerStyle = {},
  bodyId,
  bodyClassName = '',
  bodyStyle = {},
  headerColumns = [],
  bodyColumns = [],
  data = [],
  allowEmptyData = true,
  readOnly = false,
  emptyDataClassName = '',
  bodyFooter,
}) => {
  const refTable = useRef();
  const refBody = useRef();

  useEffect(
    () => {
      const scrollClass = 'vertical-scroll-body-table__scroll';
      const tableEl = refTable.current;
      const bodyEl = refBody.current;
      if (
        tableEl &&
        bodyEl &&
        !iosCheck() &&
        bodyEl.scrollHeight > bodyEl.offsetHeight
      ) {
        tableEl.classList.add(scrollClass);
      } else {
        tableEl.classList.remove(scrollClass);
      }
    },
    [data, headerColumns, bodyColumns]
  );

  const renderHeader = () => {
    if (_.size(headerColumns) === 0) {
      return null;
    }
    return _.map(headerColumns, (headerItem, index) => {
      if (!headerItem) {
        return null;
      }
      const { id, className = '', style, render, text } = headerItem;
      const cellContent = _.isFunction(render) ? (
        render()
      ) : (
        <div className='value-table__label table__header-label'>{text}</div>
      );
      return (
        <td
          key={id || index}
          className={`value-table value-table--table__label ${className}`}
          style={style}
        >
          {cellContent}
        </td>
      );
    });
  };

  const renderBody = () => {
    if (bodyColumns.length === 0) {
      return null;
    }
    return _.map(data, (dataItem, index) => (
      <BodyRow
        key={_.get(data, 'id') || index}
        data={dataItem}
        bodyColumns={bodyColumns}
        readOnly={readOnly}
      />
    ));
  };

  return (
    <div>
      <table
        className={`table vertical-scroll-body-table ${tableClassName}`}
        style={tableStyle}
        ref={refTable}
      >
        <thead
          className={`table__header ${headerClassName}`}
          style={headerStyle}
        >
          <tr className='table__label'>{renderHeader()}</tr>
        </thead>
        <tbody
          className={`table__body ${bodyClassName}`}
          style={bodyStyle}
          ref={refBody}
          id={bodyId}
        >
          {renderBody()}
          {bodyFooter}
        </tbody>
      </table>
      {data.length === 0 && allowEmptyData && (
        <div className={`table__message ${emptyDataClassName}`}>
          {messageUtility.getMessage('I-000-005')}
        </div>
      )}
    </div>
  );
};

VerticalScrollBodyTable.propTypes = {
  tableClassName: PropTypes.string,
  tableStyle: PropTypes.object,
  headerClassName: PropTypes.string,
  headerStyle: PropTypes.object,
  bodyClassName: PropTypes.string,
  bodyStyle: PropTypes.object,
  headerColumns: PropTypes.array,
  bodyColumns: PropTypes.array,
  data: PropTypes.array,
  allowEmptyData: PropTypes.bool,
};

const BodyRow = ({ bodyColumns, data, readOnly }) => {
  return (
    <tr
      className={`table__item ${
        _.get(data, 'invalid_flag') && !readOnly ? 'table__item--error' : ''
      } ${_.get(data, 'deleted_flag') ? 'table__item--suspension' : ''}`}
    >
      {_.map(bodyColumns, (bodyItem, index) => (
        <BodyCell key={bodyItem.id || index} bodyItem={bodyItem} data={data} />
      ))}
    </tr>
  );
};

BodyRow.propTypes = {
  bodyColumns: PropTypes.array,
  data: PropTypes.object,
  readOnly: PropTypes.bool,
};

const BodyCell = ({ bodyItem, data }) => {
  const {
    render,
    valueString,
    defaultValue = '',
    className = '',
    style,
    useTooltip = true,
  } = bodyItem;

  let cell;
  if (_.isFunction(render)) {
    cell = render(data);
  } else if (!useTooltip) {
    cell = _.get(data, valueString, defaultValue);
  } else {
    cell = renderToolTip(_.get(data, valueString, defaultValue));
  }

  return (
    <td className={`value-table ${className}`} style={style}>
      {cell}
    </td>
  );
};

BodyCell.propTypes = {
  valueString: PropTypes.array,
  data: PropTypes.object,
};

export const renderToolTip = value => (
  <Tippy
    duration={10}
    distance={24}
    arrowType='sharp'
    delay={[500, 0]}
    placement='bottom'
    followCursor={true}
    role={`tippy_target_${_.uniqueId()}`}
    html={renderTooltipContent(value)}
  >
    <p className={`value-table__txt--7`}>{value}</p>
  </Tippy>
);

export const renderTooltipContent = data => {
  return (
    <div
      style={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        color: '#FFF',
        fontSize: '14px',
        lineHeight: '1.5',
        textAlign: 'left',
        maxWidth: '240px',
      }}
    >
      {data}
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/tables/VerticalScrollBodyTable.jsx